import { Voni } from '../client';

export interface ChatAction {
  action: 'click' | 'fill' | 'scroll' | 'navigate' | 'submit' | 'external_tool' | 'translate';
  selector?: string;
  value?: string;
  name?: string;
  arguments?: Record<string, unknown>;
  instructions?: string;
  reason?: string;
  type?: string;
}

export interface ChatResponse {
  response: string;
  actions: ChatAction[];
  conversation_id: string;
  session_id: string;
}

export interface ChatOptions {
  message: string;
  sessionId?: string;
  pageUrl?: string;
  pageTitle?: string;
  metadata?: Record<string, unknown>;
}

export class ChatResource {
  constructor(private client: Voni) {}

  /**
   * Send a message to a project's AI engine (Headless mode).
   */
  async sendMessage(projectId: string, options: ChatOptions): Promise<ChatResponse> {
    const data = {
      message: options.message,
      session_id: options.sessionId,
      page_url: options.pageUrl,
      page_title: options.pageTitle,
      metadata: options.metadata || {},
    };
    return this.client.request<ChatResponse>('POST', `/widget/${projectId}/chat`, undefined, data);
  }

  /**
   * High-level orchestrator helper that sends a message and executes actions via provided handlers.
   */
  async orchestrate(
    projectId: string, 
    options: ChatOptions, 
    handlers: { [K in ChatAction['action'] | string]?: (action: ChatAction) => Promise<unknown> | unknown },
    interceptors: Array<(action: ChatAction) => Promise<ChatAction | boolean> | ChatAction | boolean> = []
  ): Promise<ChatResponse> {
    const response = await this.sendMessage(projectId, options);
    
    if (response.actions && response.actions.length > 0) {
      for (let action of response.actions) {
        // Run interceptors
        let cancelled = false;
        for (const interceptor of interceptors) {
          const result = await interceptor(action);
          if (result === false) {
            cancelled = true;
            break;
          }
          if (typeof result === 'object') {
            action = result;
          }
        }

        if (cancelled) continue;

        const handler = handlers[action.action] || handlers[action.name || ''];
        if (handler) {
          await handler(action);
        }
      }
    }
    
    return response;
  }
}
